import { useEffect, useState } from 'react'

import { api } from '../../lib/api'
import { color, radius } from '../../theme/tokens'
import type { Check as CheckModel, DryRunReply, PromptLine, ValidationResult } from '../../lib/types'
import { Button } from '../ui'

type Tab = 'prompt' | 'dry' | 'val'

const SEGMENT_COLORS = {
  static: { color: color.inkMuted, fontWeight: 400 },
  form: { color: color.brandHover, fontWeight: 500 },
  hard: { color: color.red, fontWeight: 500 },
  comment: { color: color.inkFaint, fontWeight: 500 },
} as const

const CHECK_TINTS = {
  ok: { icon: '✓', fg: color.greenInk, bg: color.greenTintSoft, bd: color.greenBorder },
  warn: { icon: '⚠', fg: color.amberInk, bg: color.amberTint, bd: color.brandBorder },
  err: { icon: '✕', fg: color.redDeep, bg: color.redTint, bd: color.redBorder },
} as const

interface DryTurn {
  who: 'cli' | 'ai'
  text: string
  rules: string[]
}

/**
 * Painel direito: o prompt compilado, o dry-run e o validador.
 *
 * O prompt não é editável de propósito — ele é *derivado*. Editar prompt à mão
 * é exatamente o que este produto existe para eliminar.
 */
export function InspectorPanel({
  prompt,
  validation,
}: {
  prompt: PromptLine[]
  validation: ValidationResult
}) {
  const [tab, setTab] = useState<Tab>('prompt')
  const [dry, setDry] = useState<DryTurn[]>([])
  const [input, setInput] = useState('')
  const [suggestions, setSuggestions] = useState<string[]>([])
  const [busy, setBusy] = useState(false)
  const [copied, setCopied] = useState(false)

  useEffect(() => {
    api.dryRunSuggestions().then(setSuggestions).catch(() => setSuggestions([]))
  }, [])

  const send = async (message: string) => {
    const text = message.trim()
    if (!text || busy) return
    setBusy(true)
    setInput('')
    setDry((current) => [...current, { who: 'cli', text, rules: [] }])
    try {
      const reply: DryRunReply = await api.dryRun(text)
      setDry((current) => [...current, { who: 'ai', text: reply.text, rules: reply.rules }])
    } catch (error) {
      setDry((current) => [
        ...current,
        {
          who: 'ai',
          text: error instanceof Error ? error.message : 'Falha no dry-run',
          rules: [],
        },
      ])
    } finally {
      setBusy(false)
    }
  }

  const copyPrompt = async () => {
    const text = prompt.map((line) => line.parts.map((part) => part.text).join('')).join('\n')
    try {
      await navigator.clipboard.writeText(text)
      setCopied(true)
      window.setTimeout(() => setCopied(false), 1800)
    } catch {
      setCopied(false)
    }
  }

  const badge =
    validation.errors > 0
      ? `· ${validation.errors} erro${validation.errors > 1 ? 's' : ''}`
      : validation.warnings > 0
        ? `· ${validation.warnings} avisos`
        : '· ok'

  return (
    <div
      style={{
        width: 420,
        flex: 'none',
        background: color.surface,
        borderLeft: `1px solid ${color.border}`,
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
      }}
    >
      <div style={{ display: 'flex', gap: 4, padding: '12px 12px 0', flex: 'none' }}>
        <TabButton active={tab === 'prompt'} onClick={() => setTab('prompt')}>
          Prompt compilado
        </TabButton>
        <TabButton active={tab === 'dry'} onClick={() => setTab('dry')}>
          Dry-run
        </TabButton>
        <TabButton active={tab === 'val'} onClick={() => setTab('val')}>
          Validador {badge}
        </TabButton>
      </div>

      <div
        style={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          borderTop: `1px solid ${color.border}`,
          padding: '14px 16px 84px',
        }}
      >
        {tab === 'prompt' ? (
          <div>
            <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
              <Button onClick={copyPrompt} style={{ flex: 1, padding: 8, fontSize: 11.5 }}>
                {copied ? 'Copiado ✓' : 'Copiar'}
              </Button>
            </div>
            <div
              style={{
                display: 'flex',
                gap: 12,
                marginBottom: 12,
                fontSize: 10.5,
                color: color.inkSubtle,
                flexWrap: 'wrap',
              }}
            >
              <Legend swatch={color.inkMuted}>de formulário</Legend>
              <Legend swatch={color.brandHover}>variável interpolada</Legend>
              <Legend swatch={color.red}>regra rígida</Legend>
            </div>
            <div
              style={{
                fontFamily: "'Roboto Mono', monospace",
                fontSize: 11,
                lineHeight: 1.75,
                background: color.surfaceMuted,
                border: `1px solid ${color.border}`,
                borderRadius: radius.lg,
                padding: 14,
              }}
            >
              {prompt.map((line, index) => (
                <div
                  key={index}
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    display: 'flex',
                    flexWrap: 'wrap',
                    minHeight: '1.75em',
                  }}
                >
                  {line.parts.map((part, partIndex) => (
                    <span key={partIndex} style={SEGMENT_COLORS[part.kind]}>
                      {part.text}
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </div>
        ) : null}

        {tab === 'dry' ? (
          <div>
            <div
              style={{
                fontSize: 11.5,
                color: color.inkSubtle,
                lineHeight: 1.5,
                marginBottom: 12,
              }}
            >
              Teste a política antes de publicar. Cada resposta mostra a regra que a autorizou.
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 12 }}>
              {dry.map((turn, index) => {
                const isAi = turn.who === 'ai'
                return (
                  <div key={index} style={{ animation: 'mat .28s ease both' }}>
                    <div
                      style={{
                        fontSize: 10,
                        fontWeight: 700,
                        letterSpacing: '.06em',
                        color: isAi ? color.brandHover : color.inkSubtle,
                        marginBottom: 4,
                      }}
                    >
                      {isAi ? 'AGENTE' : 'CLIENTE (TESTE)'}
                    </div>
                    <div
                      style={{
                        fontSize: 12.5,
                        lineHeight: 1.55,
                        padding: '10px 12px',
                        borderRadius: radius.lg,
                        background: isAi ? color.brandTint : color.surface,
                        border: `1px solid ${isAi ? color.brandBorder : color.border}`,
                        borderLeft: `3px solid ${isAi ? color.brand : color.inkGhost}`,
                      }}
                    >
                      {turn.text}
                    </div>
                    {turn.rules.map((rule, ruleIndex) => (
                      <div
                        key={ruleIndex}
                        style={{
                          fontFamily: "'Roboto Mono', monospace",
                          fontSize: 10,
                          color: color.inkSubtle,
                          marginTop: 5,
                          paddingLeft: 2,
                        }}
                      >
                        ⌐ {rule}
                      </div>
                    ))}
                  </div>
                )
              })}
            </div>

            <div style={{ display: 'flex', gap: 7 }}>
              <input
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') void send(input)
                }}
                placeholder="Escreva como a cliente…"
                aria-label="Mensagem de teste"
                style={{
                  flex: 1,
                  padding: '10px 12px',
                  border: `1px solid ${color.border}`,
                  borderRadius: radius.md,
                  fontSize: 12.5,
                }}
              />
              <Button
                variant="primary"
                onClick={() => void send(input)}
                disabled={busy}
                style={{ padding: '0 15px' }}
              >
                Testar
              </Button>
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
              {suggestions.map((suggestion) => (
                <button
                  key={suggestion}
                  type="button"
                  onClick={() => void send(suggestion)}
                  style={{
                    fontSize: 11,
                    padding: '5px 10px',
                    borderRadius: radius.pill,
                    background: color.brandTint,
                    color: color.brandInk,
                    border: `1px solid ${color.brandBorder}`,
                    cursor: 'pointer',
                  }}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : null}

        {tab === 'val' ? (
          <div>
            <div
              style={{
                fontSize: 11.5,
                color: color.inkSubtle,
                lineHeight: 1.5,
                marginBottom: 12,
              }}
            >
              Checagens executadas a cada alteração. Publicar fica bloqueado enquanto houver erro.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {validation.checks.map((check: CheckModel, index) => {
                const tint = CHECK_TINTS[check.state]
                return (
                  <div
                    key={index}
                    style={{
                      display: 'flex',
                      gap: 9,
                      alignItems: 'flex-start',
                      padding: '10px 12px',
                      borderRadius: radius.lg,
                      background: tint.bg,
                      border: `1px solid ${tint.bd}`,
                    }}
                  >
                    <span
                      style={{ fontSize: 12, fontWeight: 700, color: tint.fg, flex: 'none', width: 13 }}
                      aria-hidden
                    >
                      {tint.icon}
                    </span>
                    <div>
                      <div style={{ fontSize: 12.5, fontWeight: 600, color: tint.fg }}>
                        {check.label}
                      </div>
                      <div
                        style={{
                          fontSize: 11.5,
                          color: color.inkSubtle,
                          lineHeight: 1.45,
                          marginTop: 2,
                        }}
                      >
                        {check.note}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  )
}

function TabButton({
  active,
  onClick,
  children,
}: {
  active: boolean
  onClick(): void
  children: React.ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        flex: 1,
        border: 'none',
        borderRadius: '9px 9px 0 0',
        padding: '10px 8px',
        fontSize: 12,
        fontWeight: 700,
        cursor: 'pointer',
        background: active ? color.surface : '#f2f4f7',
        color: active ? color.brandHover : color.inkSubtle,
      }}
    >
      {children}
    </button>
  )
}

function Legend({ swatch, children }: { swatch: string; children: React.ReactNode }) {
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
      <span style={{ width: 9, height: 9, borderRadius: 2, background: swatch }} />
      {children}
    </span>
  )
}
